import React, { useMemo, useState } from "react";
import type {
  ClinicalEvent,
  ClinicalEventKind,
  ClinicalTimelineDensityMode,
} from "../types/clinicalEvent";
import "./ClinicalTimelinePanel.css";

const KIND_OPTIONS: Array<{ kind: ClinicalEventKind; label: string }> = [
  { kind: "annotation", label: "Annotations" },
  { kind: "medication", label: "Medications" },
  { kind: "vital", label: "Vitals" },
  { kind: "video", label: "Video" },
  { kind: "upload_milestone", label: "Uploads" },
  { kind: "conversion_milestone", label: "Conversions" },
  { kind: "report_action", label: "Reports" },
  { kind: "artifact", label: "Artifacts" },
  { kind: "qeeg_marker", label: "qEEG" },
  { kind: "bookmark", label: "Bookmarks" },
  { kind: "clip", label: "Clips" },
  { kind: "ai_detection", label: "AI" },
];

export type ClinicalTimelinePanelProps = {
  events: ClinicalEvent[];
  currentTimeSeconds: number;
  densityMode: ClinicalTimelineDensityMode;
  onDensityModeChange: (mode: ClinicalTimelineDensityMode) => void;
  enabledKinds: ClinicalEventKind[];
  onEnabledKindsChange: (kinds: ClinicalEventKind[]) => void;
  onJumpToEvent: (event: ClinicalEvent) => void;
};

export default function ClinicalTimelinePanel({
  events,
  currentTimeSeconds,
  densityMode,
  onDensityModeChange,
  enabledKinds,
  onEnabledKindsChange,
  onJumpToEvent,
}: ClinicalTimelinePanelProps) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const enabled = new Set(enabledKinds);
    return events
      .filter((event) => enabled.has(event.kind))
      .filter((event) => {
        if (!query.trim()) return true;
        const q = query.toLowerCase();
        return (
          event.eventLabel.toLowerCase().includes(q) ||
          event.eventTypeCode.toLowerCase().includes(q) ||
          event.kind.includes(q)
        );
      })
      .sort((a, b) => a.startTimeSeconds - b.startTimeSeconds);
  }, [events, enabledKinds, query]);

  const rowLimit =
    densityMode === "compact" ? 8 : densityMode === "expanded" ? 80 : 24;

  const toggleKind = (kind: ClinicalEventKind) => {
    if (enabledKinds.includes(kind)) {
      onEnabledKindsChange(enabledKinds.filter((item) => item !== kind));
    } else {
      onEnabledKindsChange([...enabledKinds, kind]);
    }
  };

  return (
    <div className="clinical-timeline-panel" data-testid="clinical-timeline-panel">
      <div className="eeg-dock-panel-chrome__toolbar clinical-timeline-panel__toolbar">
        <div className="clinical-timeline-panel__filters" role="group" aria-label="Timeline row filters">
          {KIND_OPTIONS.map((option) => (
            <label key={option.kind} className="clinical-timeline-panel__chip">
              <input
                type="checkbox"
                checked={enabledKinds.includes(option.kind)}
                onChange={() => toggleKind(option.kind)}
              />
              {option.label}
            </label>
          ))}
        </div>
        <label className="clinical-timeline-panel__density">
          Density
          <select
            value={densityMode}
            aria-label="Event density mode"
            onChange={(event) =>
              onDensityModeChange(
                event.target.value as ClinicalTimelineDensityMode,
              )
            }
          >
            <option value="compact">Compact</option>
            <option value="normal">Normal</option>
            <option value="expanded">Expanded</option>
          </select>
        </label>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter events"
          aria-label="Filter timeline events"
        />
      </div>

      <div className="clinical-timeline-panel__cursor">
        Playhead {currentTimeSeconds.toFixed(2)}s · {visible.length} events
      </div>

      <div className="clinical-timeline-panel__rows">
        {visible.slice(0, rowLimit).map((event) => {
          const near =
            Math.abs(event.startTimeSeconds - currentTimeSeconds) < 1.5;
          return (
            <button
              key={event.eventId}
              type="button"
              className={`clinical-timeline-panel__row clinical-timeline-panel__row--${event.kind}${near ? " is-near" : ""}`}
              onClick={() => onJumpToEvent(event)}
            >
              <span>{event.startTimeSeconds.toFixed(1)}s</span>
              <span>{event.kind}</span>
              <span>{event.eventLabel}</span>
            </button>
          );
        })}
        {!visible.length && (
          <div className="clinical-timeline-panel__empty">
            No clinical timeline events for the selected filters.
          </div>
        )}
      </div>
    </div>
  );
}
